import {memo, useMemo, Component, Suspense} from 'react';

import {Provider as ReduxProvider, useSelector} from 'react-redux';
import {StyleSheetManager, ThemeProvider} from 'styled-components';

import {selectCurrentModal, selectIsActiveBannersShown} from '~/shared/store/selectors';
import {getLocalizationService} from '~/shared/services/localisationService';
import {RouterProvider, useRouteQuery} from '~/shared/router';
import store from '~/shared/store';
import initManager from '~/shared/managers/initManager';
import usePreventBodyScrolling from '~/shared/hooks/usePreventBodyScrolling';
import {createTheme} from '~/shared/theme';
import GlobalCss from '~/shared/theme/globalCss';
import {ErrorPageComponent} from '~/errorPage';
import {createLogger} from '~/shared/logging';
import actions from '~/shared/store/actions';
import renderingForRobot from '~/shared/services/renderingForRobot';
import useDatadogContext from '~/shared/hooks/useDatadogContext';
import useBranchIO from '~/shared/hooks/useBranchIO';
import {collectWebVitalsMetrics} from '~/shared/services/analytics';

import {PersistentMetaHeaders, DefaultHeaders} from './MainMetaHeaders';
import SupportChat from './SupportChat';
import Routes from './Routes';
import AppsPromotionBanner from './AppsPromotionBanner';
import './moment';
import A11yAndCookieConcent from './A11yAndCookieConcent';

const logger = createLogger('App');

const AppContent = memo(({unexpectedError}) => {
  const {currentLanguageKey: lng, currentLanguageDirection: direction} = getLocalizationService();
  const {hideHeader} = useRouteQuery();
  const isBannersShown = useSelector(selectIsActiveBannersShown);
  const currentModal = useSelector(selectCurrentModal);

  // useUpdateJetFm();

  usePreventBodyScrolling({shouldPreventScroll: !!currentModal});
  useDatadogContext();
  useBranchIO();

  const theme = useMemo(() => {
    return createTheme({direction, hideHeader, lng});
  }, [direction, hideHeader, lng]);

  return (
    <StyleSheetManager disableCSSOMInjection={renderingForRobot}>
      <ThemeProvider theme={theme}>
        <GlobalCss isBannersShown={isBannersShown} />

        <A11yAndCookieConcent />

        <div style={{direction}}>
          <AppsPromotionBanner />
          <DefaultHeaders />
          <SupportChat />
          {unexpectedError ? <ErrorPageComponent errorDescriptionKey="unexpected_error" /> : <Routes />}
          <div id="modals" />
        </div>
      </ThemeProvider>
    </StyleSheetManager>
  );
});

class App extends Component {
  state = {
    unexpectedError: null,
  };

  static getDerivedStateFromError(error) {
    return {unexpectedError: error};
  }

  componentDidMount() {
    initManager.initApp();
    collectWebVitalsMetrics();
  }

  componentDidCatch(error, errorInfo) {
    logger.error('Unexpected error occurred', {error, errorInfo, sendReport: true});
    store.dispatch(actions.setCurrentModal(null));
  }

  render() {
    const {unexpectedError} = this.state;

    return (
      <Suspense fallback={null}>
        <ReduxProvider store={store}>
          <RouterProvider>
            <PersistentMetaHeaders />
            <AppContent unexpectedError={unexpectedError} />
          </RouterProvider>
        </ReduxProvider>
      </Suspense>
    );
  }
}

export default App;
